/** Alert rules and alarms (redesign phase 4, the Alerts board): what a rule
 *  says in words, the editor's draft, and the counts the alarm surfaces
 *  show. The server checks every rule again (services/alert_rules.py). */
import type {
  Alarm, AlertCondition, AlertNotify, AlertRule, AlertRuleBody, AlertScope,
} from '../api/types'

export const CONDITIONS: Record<AlertScope, [AlertCondition, string][]> = {
  reading: [['above', 'Value goes above'], ['below', 'Value drops below'],
            ['rate', 'Value changes faster than, per day'], ['stale', 'No new reading for']],
  station: [['nodata', 'No data: nothing new for'], ['gateway', 'Gateway unanswered for'],
            ['anyfast', 'Any channel in a group faster than']],
}

const LABEL = Object.fromEntries([...CONDITIONS.reading, ...CONDITIONS.station]) as
  Record<AlertCondition, string>

export function conditionLabel(condition: AlertCondition): string {
  return LABEL[condition]
}

/** Hold applies to Above and Below only (plan ruling 2). */
export function hasHold(condition: AlertCondition): boolean {
  return condition === 'above' || condition === 'below'
}

/** The unit beside a rule's levels. */
export function levelUnit(condition: AlertCondition, unit: string | null | undefined): string {
  switch (condition) {
    case 'rate':
    case 'anyfast': return unit ? `${unit}/day` : '/day'
    case 'stale': return 'min'
    case 'nodata': return '× interval'
    case 'gateway': return 'h'
    default: return unit ?? ''
  }
}

function withUnit(value: number, unit: string): string {
  return unit.startsWith('×') || unit.startsWith('/') ? `${value} ${unit}`.replace(' /', '/')
    : `${value}${unit ? ` ${unit}` : ''}`
}

export interface RuleSummary {
  condition: string
  warn: string | null
  alarm: string
  tells: string
  scope: string
}

/** One row of the Alert Rules table. */
export function ruleSummary(rule: AlertRule): RuleSummary {
  const unit = levelUnit(rule.condition, rule.parameter?.unit)
  const held = rule.hold_s && hasHold(rule.condition) ? `, held ${Math.round(rule.hold_s / 60)} min` : ''
  const names = [...rule.targets.projects.map((p) => `${p.name} members`),
                 ...rule.targets.users.map((u) => u.name)]
  return {
    condition: `${conditionLabel(rule.condition)}${held}`,
    warn: rule.warn_level === null ? null : withUnit(rule.warn_level, unit),
    alarm: withUnit(rule.alarm_level, unit),
    tells: names.join(', ') || 'Nobody',
    scope: rule.scope === 'reading' ? 'Per reading' : 'Per station',
  }
}

export function scopeCounts(rules: AlertRule[]): Record<'all' | AlertScope, number> {
  return { all: rules.length, reading: rules.filter((r) => r.scope === 'reading').length,
           station: rules.filter((r) => r.scope === 'station').length }
}

/** The sentence under the levels, as the board words it; `bad` stops Save.
 *  For a falling value (Below) the warning sits above the alarm. */
export function levelHint(condition: AlertCondition, warn: string, alarm: string):
  { text: string; bad: boolean } {
  if (alarm.trim() === '' || Number.isNaN(Number(alarm))) {
    return { text: 'An alarm level is needed. Leave Warning empty to raise only an alarm.', bad: true }
  }
  const w = Number(warn)
  if (warn.trim() !== '' && !Number.isNaN(w)) {
    const a = Number(alarm)
    if (condition === 'below' ? w <= a : w >= a) {
      return { text: condition === 'below'
        ? 'Warning must sit above the alarm level for a falling value.'
        : 'Warning must sit below the alarm level.', bad: true }
    }
  }
  return { text: 'Leave Warning empty to raise only an alarm.', bad: false }
}

export interface Draft {
  id?: number
  name: string
  scope: AlertScope
  station: string
  parameter: string
  axis_group: string
  condition: AlertCondition
  warn: string
  alarm: string
  holdMin: string
  message: string
  notify_on: AlertNotify[]
  users: number[]
  projects: number[]
  enabled: boolean
}

export function emptyDraft(station = ''): Draft {
  return { name: '', scope: 'reading', station, parameter: '', axis_group: '', condition: 'above',
           warn: '', alarm: '', holdMin: '0', message: '', notify_on: ['warning', 'alarm', 'cleared'],
           users: [], projects: [], enabled: true }
}

export function draftFrom(rule: AlertRule): Draft {
  return {
    id: rule.id, name: rule.name, scope: rule.scope, station: rule.station.code,
    parameter: rule.parameter?.code ?? '', axis_group: rule.axis_group ?? '',
    condition: rule.condition, warn: rule.warn_level === null ? '' : String(rule.warn_level),
    alarm: String(rule.alarm_level), holdMin: String(Math.round(rule.hold_s / 60)),
    message: rule.message, notify_on: [...rule.notify_on],
    users: rule.targets.users.map((u) => u.id), projects: rule.targets.projects.map((p) => p.id),
    enabled: rule.enabled,
  }
}

/** What the API is sent: only what the condition uses. */
export function toBody(draft: Draft): AlertRuleBody {
  const reading = draft.scope === 'reading'
  return {
    name: draft.name.trim(), scope: draft.scope, station: draft.station,
    parameter: reading && draft.parameter ? draft.parameter : null,
    axis_group: draft.condition === 'anyfast' && draft.axis_group ? draft.axis_group : null,
    condition: draft.condition,
    warn_level: draft.warn.trim() === '' ? null : Number(draft.warn),
    alarm_level: Number(draft.alarm),
    hold_s: hasHold(draft.condition) ? Math.max(0, Math.round(Number(draft.holdMin || 0) * 60)) : 0,
    message: draft.message.trim(), notify_on: draft.notify_on, enabled: draft.enabled,
    targets: { users: draft.users, projects: draft.projects },
  }
}

// ------------------------------------------------------------------ alarms

const RULES_SILENT_MS = 180_000

/** Whether the alarms shown are current: the rule monitor beat in the last
 *  three minutes (the health checks' worker_silent threshold). Otherwise
 *  "no alarm" would be a lie, and viewers cannot see Intake. */
export function alarmsChecked(checkedAt: string | null | undefined, now = new Date()): boolean {
  return !!checkedAt && now.getTime() - new Date(checkedAt).getTime() < RULES_SILENT_MS
}

export type AlarmFilter = 'all' | 'station' | 'channel'

export function alarmCounts(alarms: Alarm[]) {
  return {
    alarms: alarms.filter((a) => a.level === 'alarm').length,
    warnings: alarms.filter((a) => a.level === 'warning').length,
    channel: alarms.filter((a) => a.rule.scope === 'reading').length,
    station: alarms.filter((a) => a.rule.scope === 'station').length,
    strategy: alarms.filter((a) => a.rule.scope === 'strategy').length,
  }
}

/** "2 alarms, 1 warning. 1 on a channel, 1 on a whole station." A strategy's
 *  alarms are named only when there are some. */
export function alarmCountsText(c: ReturnType<typeof alarmCounts>): string {
  const strategies = c.strategy ? `, ${c.strategy} from ${c.strategy === 1 ? 'a strategy' : 'strategies'}` : ''
  return `${c.alarms} ${c.alarms === 1 ? 'alarm' : 'alarms'}, ${c.warnings} ${c.warnings === 1 ? 'warning' : 'warnings'}. `
    + `${c.channel} on a channel, ${c.station} on a whole station${strategies}.`
}

/** Station and Channel each show their own rules; a strategy's alarm shows under All. */
export function filterAlarms(alarms: Alarm[], filter: AlarmFilter): Alarm[] {
  if (filter === 'all') return alarms
  return alarms.filter((a) => a.rule.scope === (filter === 'channel' ? 'reading' : 'station'))
}

export function alarmScopeLabel(alarm: Alarm): string {
  const scope = alarm.rule.scope === 'reading' ? 'Channel' : alarm.rule.scope === 'strategy' ? 'Strategy' : 'Station'
  return `${scope} ${alarm.level}`
}

export function levelWord(level: Alarm['level']): string {
  return level === 'alarm' ? 'Alarm' : 'Warning'
}

/** The stations an alarm is about: its rule's station, or every station a
 *  strategy reads (db/036; a strategy's alarm has no station of its own). */
export function alarmStations(a: Pick<Alarm, 'station' | 'stations'>): { code: string; name: string }[] {
  return a.station ? [a.station] : a.stations ?? []
}

/** Where an alarm is, in words: its station, or a strategy's project. */
export function alarmWhere(a: Pick<Alarm, 'station' | 'project'>): string {
  return a.station?.name ?? (a.project ? `${a.project.name} (strategy)` : 'Strategy')
}

/** Where an alarm leads: its station's readings, or the strategy in the Lab. */
export function alarmLink(a: Pick<Alarm, 'station' | 'rule'>): string {
  return a.station ? `/explore/${encodeURIComponent(a.station.code)}` : `/alerts?tab=lab&strategy=${a.rule.id}`
}

/** The open alarms per station code, alarms before warnings. A strategy's
 *  alarm stands under every station it reads. */
export function alarmsByStation(alarms: Alarm[]): Map<string, Alarm[]> {
  const out = new Map<string, Alarm[]>()
  for (const a of alarms) for (const s of alarmStations(a)) out.set(s.code, [...(out.get(s.code) ?? []), a])
  for (const list of out.values()) list.sort((x, y) => (x.level === y.level ? 0 : x.level === 'alarm' ? -1 : 1))
  return out
}

/** "2 alarms" / "1 warning" for a pill. */
export function alarmPill(alarms: Alarm[]): { text: string; tone: 'alarm' | 'warning' | 'none' } {
  const n = alarms.filter((a) => a.level === 'alarm').length
  if (n) return { text: `${n} ${n === 1 ? 'alarm' : 'alarms'}`, tone: 'alarm' }
  const w = alarms.length
  if (w) return { text: `${w} ${w === 1 ? 'warning' : 'warnings'}`, tone: 'warning' }
  return { text: 'No alarm', tone: 'none' }
}
